import React, { useCallback, useState, createElement } from 'react';
import { useParams, Link } from 'react-router-dom';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useRestaurantBySlug } from '../../lib/hooks';
import { useTenant } from '../../lib/tenant-context';
import { useTableManagement, useTableMetrics, useTableOrderItems, closeTableAccount } from '../../lib/table-hooks';
import { Card, CardContent } from '../../components/ui/Card';
import { Skeleton } from '../../components/ui/Skeleton';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { cn, formatCurrency } from '../../lib/utils';
import { TableWithOrder, TableStatus, OrderChannel } from '../../lib/types';
import { Armchair, QrCode, User, Clock, ShoppingBag, Hash, Users, Receipt, Store, Smartphone, MonitorSmartphone, CreditCard, Banknote, X, Loader2, ChefHat } from 'lucide-react';
interface Toast {
  id: number;
  type: 'success' | 'error';
  message: string;
}
export function TableManagementPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const {
    restaurant,
    loading: restLoading
  } = useRestaurantBySlug(slug);
  const tenantCtx = useTenant();
  const {
    tables,
    loading: tablesLoading,
    refetch: refetchTables
  } = useTableManagement(restaurant?.id);
  const {
    metrics,
    loading: metricsLoading
  } = useTableMetrics(restaurant?.id);
  const [selectedTable, setSelectedTable] = useState<TableWithOrder | null>(null);
  const [selectedPayment, setSelectedPayment] = useState<'cash' | 'card' | 'sinpe'>('cash');
  const [isClosing, setIsClosing] = useState(false);
  const [toasts, setToasts] = useState<Toast[]>([]);
  // Fetch items only when a table is selected
  const {
    items: orderItems,
    loading: itemsLoading
  } = useTableOrderItems(selectedTable?.id, restaurant?.id);
  const addToast = useCallback((type: 'success' | 'error', message: string) => {
    const id = Date.now();
    setToasts(prev => [...prev, {
      id,
      type,
      message
    }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 3500);
  }, []);
  const removeToast = useCallback((id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);
  const handleCloseAccount = async () => {
    if (!restaurant || !selectedTable || !selectedTable.current_order) return;
    if (!window.confirm(`¿Estás seguro de cerrar la cuenta de la ${selectedTable.table_name || selectedTable.table_number}?`)) {
      return;
    }
    setIsClosing(true);
    try {
      const result = await closeTableAccount(restaurant.id, selectedTable.id, selectedPayment, tenantCtx?.userId || null);
      if (result.success) {
        addToast('success', 'Cuenta cerrada correctamente');
        setSelectedTable(null);
        refetchTables();
      } else {
        addToast('error', result.error || 'Error al cerrar la cuenta');
      }
    } catch (error: any) {
      addToast('error', error.message || 'Error inesperado');
    } finally {
      setIsClosing(false);
    }
  };
  if (restLoading || !restaurant) {
    return <AdminLayout restaurant={restaurant} role={tenantCtx?.role} onSignOut={tenantCtx?.signOut} data-id="element-1952">
        <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center p-6" data-id="element-1953">
          <div className="w-16 h-16 rounded-full bg-[#141414] animate-pulse" data-id="element-1954" />
        </div>
      </AdminLayout>;
  }
  const getStatusColor = (status: TableStatus) => {
    switch (status) {
      case 'free':
        return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20';
      case 'occupied':
        return 'bg-amber-500/10 text-amber-400 border-amber-500/20';
      case 'ordering':
        return 'bg-blue-500/10 text-blue-400 border-blue-500/20';
      case 'waiting_payment':
        return 'bg-purple-500/10 text-purple-400 border-purple-500/20';
      case 'closed':
        return 'bg-gray-500/10 text-gray-400 border-gray-500/20';
      default:
        return 'bg-gray-500/10 text-gray-400 border-gray-500/20';
    }
  };
  const getStatusLabel = (status: TableStatus) => {
    switch (status) {
      case 'free':
        return 'Disponible';
      case 'occupied':
        return 'Ocupada';
      case 'ordering':
        return 'Ordenando';
      case 'waiting_payment':
        return 'Por Pagar';
      case 'closed':
        return 'Cerrada';
      default:
        return 'Desconocido';
    }
  };
  const getSourceInfo = (channel: OrderChannel | null) => {
    switch (channel) {
      case 'qr':
        return {
          label: 'QR',
          icon: QrCode,
          color: 'bg-emerald-500/20 text-emerald-400'
        };
      case 'waiter':
        return {
          label: 'Mesero',
          icon: User,
          color: 'bg-blue-500/20 text-blue-400'
        };
      case 'cashier':
        return {
          label: 'Caja',
          icon: Store,
          color: 'bg-amber-500/20 text-amber-400'
        };
      case 'web':
        return {
          label: 'Web',
          icon: MonitorSmartphone,
          color: 'bg-gray-500/20 text-gray-400'
        };
      default:
        return {
          label: 'Desconocido',
          icon: Smartphone,
          color: 'bg-gray-500/20 text-gray-400'
        };
    }
  };
  const getItemStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-gray-100 text-gray-600';
      case 'preparing':
        return 'bg-amber-100 text-amber-700';
      case 'ready':
        return 'bg-emerald-100 text-emerald-700';
      case 'served':
        return 'bg-blue-100 text-blue-700';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };
  const getItemStatusLabel = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Pendiente';
      case 'preparing':
        return 'Preparando';
      case 'ready':
        return 'Listo';
      case 'served':
        return 'Servido';
      default:
        return status;
    }
  };
  return <AdminLayout restaurant={restaurant} role={tenantCtx?.role} onSignOut={tenantCtx?.signOut} data-id="element-1955">
      <div className="min-h-screen bg-[#0B0B0B] p-4 sm:p-6 text-white font-sans relative" data-id="element-1956">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6" data-id="element-1957">
          <div data-id="element-1958">
            <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight flex items-center gap-3" data-id="element-1959">
              Gestión de Mesas
              {!metricsLoading && <span className="bg-white/10 text-white/70 px-3 py-1 rounded-full text-sm font-bold tabular-nums" data-id="element-1960">
                  {metrics.activeTables} / {metrics.totalTables} activas
                </span>}
            </h1>
            <p className="text-white/50 text-sm mt-1" data-id="element-1961">
              Vista general de la operación en salón y autoatención
            </p>
          </div>
          <Link to={`/admin/${restaurant.slug}/mesas/qr`} className="flex items-center gap-2 px-4 py-2.5 bg-white/10 hover:bg-white/15 text-white font-bold text-sm rounded-xl border border-white/10 transition-all" data-id="element-1962">
            <QrCode className="w-4 h-4" data-id="element-1963" />
            Códigos QR
          </Link>
        </div>

        {/* Metrics Row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-8" data-id="element-1964">
          {metricsLoading ? [1, 2, 3, 4].map(i => <div key={i} className="h-28 rounded-2xl bg-[#141414] animate-pulse border border-white/5" data-id="element-1965" />) : <>
              <div className="bg-[#141414] rounded-2xl p-4 border-l-4 border-y border-r border-white/5 border-l-emerald-500 shadow-lg" data-id="element-1966">
                <div className="flex items-center justify-between mb-2" data-id="element-1967">
                  <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center" data-id="element-1968">
                    <QrCode className="w-4 h-4 text-emerald-400" data-id="element-1969" />
                  </div>
                  <span className="text-2xl font-black tabular-nums" data-id="element-1970">
                    {metrics.qrOrdersToday}
                  </span>
                </div>
                <div className="flex items-center justify-between" data-id="element-1971">
                  <p className="text-sm font-bold text-white/50" data-id="element-1972">Pedidos QR</p>
                  <span className="text-xs font-bold text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-md" data-id="element-1973">
                    {metrics.selfServicePercent}% autoatención
                  </span>
                </div>
              </div>

              <div className="bg-[#141414] rounded-2xl p-4 border-l-4 border-y border-r border-white/5 border-l-blue-500 shadow-lg" data-id="element-1974">
                <div className="flex items-center justify-between mb-2" data-id="element-1975">
                  <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center" data-id="element-1976">
                    <User className="w-4 h-4 text-blue-400" data-id="element-1977" />
                  </div>
                  <span className="text-2xl font-black tabular-nums" data-id="element-1978">
                    {metrics.waiterOrdersToday}
                  </span>
                </div>
                <div className="flex items-center justify-between" data-id="element-1979">
                  <p className="text-sm font-bold text-white/50" data-id="element-1980">
                    Pedidos Mesero
                  </p>
                  <span className="text-xs font-bold text-blue-400 bg-blue-500/10 px-2 py-0.5 rounded-md" data-id="element-1981">
                    {metrics.waiterPercent}% atención
                  </span>
                </div>
              </div>

              <div className="bg-[#141414] rounded-2xl p-4 border-l-4 border-y border-r border-white/5 border-l-amber-500 shadow-lg" data-id="element-1982">
                <div className="flex items-center justify-between mb-2" data-id="element-1983">
                  <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center" data-id="element-1984">
                    <Armchair className="w-4 h-4 text-amber-400" data-id="element-1985" />
                  </div>
                  <span className="text-2xl font-black tabular-nums" data-id="element-1986">
                    {metrics.activeTables}
                  </span>
                </div>
                <div className="flex items-center justify-between" data-id="element-1987">
                  <p className="text-sm font-bold text-white/50" data-id="element-1988">
                    Mesas Activas
                  </p>
                  <span className="text-xs font-bold text-white/40" data-id="element-1989">
                    de {metrics.totalTables} totales
                  </span>
                </div>
              </div>

              <div className="bg-[#141414] rounded-2xl p-4 border-l-4 border-y border-r border-white/5 border-l-purple-500 shadow-lg" data-id="element-1990">
                <div className="flex items-center justify-between mb-2" data-id="element-1991">
                  <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center" data-id="element-1992">
                    <Receipt className="w-4 h-4 text-purple-400" data-id="element-1993" />
                  </div>
                  <span className="text-2xl font-black tabular-nums text-[#F4D35E]" data-id="element-1994">
                    {formatCurrency(metrics.averageTicketPerTable)}
                  </span>
                </div>
                <div className="flex items-center justify-between" data-id="element-1995">
                  <p className="text-sm font-bold text-white/50" data-id="element-1996">
                    Ticket Promedio
                  </p>
                  <span className="text-xs font-bold text-white/40" data-id="element-1997">
                    por mesa
                  </span>
                </div>
              </div>
            </>}
        </div>

        {/* Tables Grid */}
        {tablesLoading ? <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-id="element-1998">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(i => <div key={i} className="h-40 rounded-2xl bg-[#141414] animate-pulse border border-white/5" data-id="element-1999" />)}
          </div> : tables.length === 0 ? <div className="py-24 flex flex-col items-center justify-center text-white/30 bg-[#141414] rounded-2xl border border-white/5 border-dashed" data-id="element-2000">
            <Armchair className="w-16 h-16 text-white/10 mb-4" data-id="element-2001" />
            <h3 className="text-xl font-black text-white/40 mb-1" data-id="element-2002">
              No hay mesas configuradas
            </h3>
            <p className="text-sm" data-id="element-2003">
              Las mesas deben ser creadas desde la configuración del
              restaurante.
            </p>
          </div> : <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-id="element-2004">
            {tables.map(table => {
          const SourceIcon = getSourceInfo(table.order_channel).icon;
          const sourceColor = getSourceInfo(table.order_channel).color;
          const sourceLabel = getSourceInfo(table.order_channel).label;
          const isOccupied = table.status !== 'free';
          return <div key={table.id} onClick={() => setSelectedTable(table)} className={cn('bg-[#141414] rounded-2xl p-4 border border-white/5 flex flex-col cursor-pointer transition-all hover:border-white/20 hover:shadow-lg hover:-translate-y-0.5 relative overflow-hidden', isOccupied && 'border-white/10 bg-white/[0.02]')} data-id="element-2005">
                  {/* Status Indicator Line */}
                  <div className={cn('absolute top-0 left-0 right-0 h-1', table.status === 'free' && 'bg-emerald-500', table.status === 'occupied' && 'bg-amber-500', table.status === 'ordering' && 'bg-blue-500', table.status === 'waiting_payment' && 'bg-purple-500', table.status === 'closed' && 'bg-gray-500')} data-id="element-2006" />

                  <div className="flex justify-between items-start mb-3 mt-1" data-id="element-2007">
                    <div data-id="element-2008">
                      <h3 className="text-xl font-black text-white leading-none mb-1" data-id="element-2009">
                        {table.table_name || table.table_number}
                      </h3>
                      {table.zone && <span className="text-xs font-bold text-white/40 uppercase tracking-wider" data-id="element-2010">
                          {table.zone}
                        </span>}
                    </div>
                    <div className={cn('px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border', getStatusColor(table.status))} data-id="element-2011">
                      {getStatusLabel(table.status)}
                    </div>
                  </div>

                  <div className="flex items-center gap-3 text-xs text-white/40 font-medium mb-auto" data-id="element-2012">
                    {table.capacity && <span className="flex items-center gap-1" data-id="element-2013">
                        <Users className="w-3.5 h-3.5" data-id="element-2014" />
                        {table.capacity} pax
                      </span>}
                  </div>

                  {/* Order Info (if occupied) */}
                  <div className="mt-4 pt-3 border-t border-white/5" data-id="element-2015">
                    {isOccupied && table.current_order ? <>
                        <div className="flex justify-between items-end mb-2" data-id="element-2016">
                          <span className="text-lg font-black text-[#F4D35E] tabular-nums leading-none" data-id="element-2017">
                            {formatCurrency(table.current_order.total)}
                          </span>
                          {table.time_open_minutes !== null && <span className="flex items-center gap-1 text-xs font-bold text-white/50" data-id="element-2018">
                              <Clock className="w-3.5 h-3.5" data-id="element-2019" />
                              {table.time_open_minutes}m
                            </span>}
                        </div>
                        <div className="flex items-center justify-between" data-id="element-2020">
                          <span className="text-xs text-white/40 font-medium" data-id="element-2021">
                            {table.item_count} items
                          </span>
                          <div className="flex items-center gap-1.5" data-id="element-2022">
                            {(table as any).waiter_name && <span className="text-[10px] text-white/40 truncate max-w-[60px]" data-id="element-2023">
                                {(table as any).waiter_name}
                              </span>}
                            <span className={cn('flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider', sourceColor)} data-id="element-2024">
                              <SourceIcon className="w-3 h-3" data-id="element-2025" />
                              {sourceLabel}
                            </span>
                          </div>
                        </div>
                      </> : <div className="flex items-center justify-center h-10 text-xs font-bold text-white/20 uppercase tracking-wider" data-id="element-2026">
                        Mesa disponible
                      </div>}
                  </div>
                </div>;
        })}
          </div>}

        {/* Table Detail Modal */}
        <Modal isOpen={!!selectedTable} onClose={() => setSelectedTable(null)} title={selectedTable ? `Mesa ${selectedTable.table_name || selectedTable.table_number}` : ''} data-id="element-2027">
          {selectedTable && <div className="space-y-6 text-gray-900" data-id="element-2028">
              {/* Table Meta */}
              <div className="flex flex-wrap gap-2" data-id="element-2029">
                <Badge className={cn('border-0', selectedTable.status === 'free' && 'bg-emerald-100 text-emerald-800', selectedTable.status === 'occupied' && 'bg-amber-100 text-amber-800', selectedTable.status === 'ordering' && 'bg-blue-100 text-blue-800', selectedTable.status === 'waiting_payment' && 'bg-purple-100 text-purple-800', selectedTable.status === 'closed' && 'bg-gray-100 text-gray-800')} data-id="element-2030">
                  {getStatusLabel(selectedTable.status)}
                </Badge>
                {selectedTable.zone && <Badge variant="secondary" data-id="element-2031">{selectedTable.zone}</Badge>}
                {selectedTable.capacity && <Badge variant="secondary" className="flex items-center gap-1" data-id="element-2032">
                    <Users className="w-3 h-3" data-id="element-2033" />
                    {selectedTable.capacity} pax
                  </Badge>}
              </div>

              {selectedTable.current_order ? <>
                  {/* Order Summary */}
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-100" data-id="element-2034">
                    <div className="grid grid-cols-2 gap-4" data-id="element-2035">
                      <div data-id="element-2036">
                        <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1" data-id="element-2037">
                          Orden
                        </p>
                        <p className="font-bold text-gray-900 flex items-center gap-1.5" data-id="element-2038">
                          <Hash className="w-4 h-4 text-gray-400" data-id="element-2039" />
                          {selectedTable.current_order.order_number}
                        </p>
                      </div>
                      <div data-id="element-2040">
                        <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1" data-id="element-2041">
                          Cliente
                        </p>
                        <p className="font-bold text-gray-900 flex items-center gap-1.5 truncate" data-id="element-2042">
                          <User className="w-4 h-4 text-gray-400" data-id="element-2043" />
                          {selectedTable.current_order.customer_name || 'Sin nombre'}
                        </p>
                      </div>
                      <div data-id="element-2044">
                        <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1" data-id="element-2045">
                          Origen
                        </p>
                        <p className="font-bold text-gray-900 flex items-center gap-1.5" data-id="element-2046">
                          {createElement(getSourceInfo(selectedTable.order_channel).icon, {
                      className: 'w-4 h-4 text-gray-400'
                    })}
                          {getSourceInfo(selectedTable.order_channel).label}
                          {(selectedTable as any).waiter_name && <span className="text-gray-500 font-normal text-sm ml-1 truncate max-w-[100px]" data-id="element-2047">
                              ({(selectedTable as any).waiter_name})
                            </span>}
                        </p>
                      </div>
                      <div data-id="element-2048">
                        <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1" data-id="element-2049">
                          Tiempo
                        </p>
                        <p className="font-bold text-gray-900 flex items-center gap-1.5" data-id="element-2050">
                          <Clock className="w-4 h-4 text-gray-400" data-id="element-2051" />
                          {selectedTable.time_open_minutes} min
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Items List */}
                  <div data-id="element-2052">
                    <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2" data-id="element-2053">
                      <ShoppingBag className="w-4 h-4 text-gray-400" data-id="element-2054" />
                      Items del Pedido ({selectedTable.item_count})
                    </h4>
                    <div className="space-y-2 max-h-[40vh] overflow-y-auto pr-2" data-id="element-2055">
                      {itemsLoading ? <div className="flex justify-center py-4" data-id="element-2056">
                          <Loader2 className="w-6 h-6 text-gray-300 animate-spin" data-id="element-2057" />
                        </div> : orderItems.length > 0 ? orderItems.map(item => <div key={item.order_item_id} className="flex justify-between items-start py-3 border-b border-gray-100 last:border-0" data-id="element-2058">
                            <div className="flex gap-3" data-id="element-2059">
                              <span className="font-bold text-gray-900 bg-gray-100 px-2 py-0.5 rounded text-sm h-fit" data-id="element-2060">
                                {item.quantity}x
                              </span>
                              <div data-id="element-2061">
                                <p className="font-bold text-gray-900 text-sm" data-id="element-2062">
                                  {item.item_name}
                                </p>
                                <div className="flex items-center gap-2 mt-1.5" data-id="element-2063">
                                  {item.station_name && <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded" data-id="element-2064">
                                      <ChefHat className="w-3 h-3" data-id="element-2065" />
                                      {item.station_name}
                                    </span>}
                                  <span className={cn('inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider', getItemStatusBadge(item.item_status))} data-id="element-2066">
                                    {getItemStatusLabel(item.item_status)}
                                  </span>
                                </div>
                                {item.notes && <p className="text-xs text-gray-500 mt-1.5 italic" data-id="element-2067">
                                    "{item.notes}"
                                  </p>}
                              </div>
                            </div>
                            <span className="font-bold text-gray-900 text-sm tabular-nums shrink-0" data-id="element-2068">
                              {formatCurrency(item.line_total)}
                            </span>
                          </div>) : <p className="text-sm text-gray-500 italic py-2" data-id="element-2069">
                          No se pudieron cargar los items.
                        </p>}
                    </div>
                  </div>

                  {/* Total & Payment */}
                  <div className="pt-4 border-t border-gray-200" data-id="element-2070">
                    <div className="flex justify-between items-center mb-6" data-id="element-2071">
                      <span className="font-bold text-gray-900" data-id="element-2072">
                        Total a pagar
                      </span>
                      <span className="text-2xl font-black text-brand-amber tabular-nums" data-id="element-2073">
                        {formatCurrency(selectedTable.current_order.total)}
                      </span>
                    </div>

                    <div className="space-y-3" data-id="element-2074">
                      <p className="text-sm font-bold text-gray-700" data-id="element-2075">
                        Método de pago
                      </p>
                      <div className="grid grid-cols-3 gap-2" data-id="element-2076">
                        <label className={cn('flex flex-col items-center justify-center p-3 rounded-xl border-2 cursor-pointer transition-all', selectedPayment === 'cash' ? 'border-brand-amber bg-amber-50 text-brand-amber' : 'border-gray-100 hover:border-gray-200 text-gray-600')} data-id="element-2077">
                          <input type="radio" name="payment" value="cash" checked={selectedPayment === 'cash'} onChange={() => setSelectedPayment('cash')} className="sr-only" data-id="element-2078" />
                          <Banknote className="w-5 h-5 mb-1" data-id="element-2079" />
                          <span className="text-xs font-bold" data-id="element-2080">Efectivo</span>
                        </label>
                        <label className={cn('flex flex-col items-center justify-center p-3 rounded-xl border-2 cursor-pointer transition-all', selectedPayment === 'card' ? 'border-brand-amber bg-amber-50 text-brand-amber' : 'border-gray-100 hover:border-gray-200 text-gray-600')} data-id="element-2081">
                          <input type="radio" name="payment" value="card" checked={selectedPayment === 'card'} onChange={() => setSelectedPayment('card')} className="sr-only" data-id="element-2082" />
                          <CreditCard className="w-5 h-5 mb-1" data-id="element-2083" />
                          <span className="text-xs font-bold" data-id="element-2084">Tarjeta</span>
                        </label>
                        <label className={cn('flex flex-col items-center justify-center p-3 rounded-xl border-2 cursor-pointer transition-all', selectedPayment === 'sinpe' ? 'border-brand-amber bg-amber-50 text-brand-amber' : 'border-gray-100 hover:border-gray-200 text-gray-600')} data-id="element-2085">
                          <input type="radio" name="payment" value="sinpe" checked={selectedPayment === 'sinpe'} onChange={() => setSelectedPayment('sinpe')} className="sr-only" data-id="element-2086" />
                          <Smartphone className="w-5 h-5 mb-1" data-id="element-2087" />
                          <span className="text-xs font-bold" data-id="element-2088">SINPE</span>
                        </label>
                      </div>
                    </div>

                    <div className="mt-6" data-id="element-2089">
                      <Button fullWidth size="lg" onClick={handleCloseAccount} disabled={isClosing} className="bg-red-600 hover:bg-red-500 text-white font-bold shadow-lg" data-id="element-2090">
                        {isClosing ? <span className="flex items-center justify-center gap-2" data-id="element-2091">
                            <Loader2 className="w-5 h-5 animate-spin" data-id="element-2092" />
                            Cerrando cuenta...
                          </span> : 'Cerrar Cuenta'}
                      </Button>
                    </div>
                  </div>
                </> : <div className="py-12 flex flex-col items-center justify-center text-gray-400 bg-gray-50 rounded-xl border border-gray-100 border-dashed" data-id="element-2093">
                  <Armchair className="w-12 h-12 text-gray-300 mb-3" data-id="element-2094" />
                  <p className="font-medium text-gray-600" data-id="element-2095">Mesa disponible</p>
                  <p className="text-sm" data-id="element-2096">
                    No hay ninguna orden activa en esta mesa.
                  </p>
                </div>}
            </div>}
        </Modal>

        {/* Toast Notifications */}
        {toasts.length > 0 && <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-2 pointer-events-none" data-id="element-2097">
            {toasts.map(toast => <div key={toast.id} className={cn('pointer-events-auto flex items-center gap-3 px-5 py-3 rounded-xl shadow-2xl border text-sm font-bold min-w-[280px] max-w-[400px] anim-slide-up', toast.type === 'success' ? 'bg-emerald-600 border-emerald-500 text-white' : 'bg-red-600 border-red-500 text-white')} data-id="element-2098">
                <span className="flex-1" data-id="element-2099">{toast.message}</span>
                <button onClick={() => removeToast(toast.id)} className="p-0.5 hover:bg-white/20 rounded transition-colors shrink-0" data-id="element-2100">
                  <X className="w-4 h-4" data-id="element-2101" />
                </button>
              </div>)}
          </div>}
      </div>
    </AdminLayout>;
}